/**
 * Overlay Manager
 * 
 * Controls which overlay is active. Only one overlay can be active at a time.
 * Overlays: 'work', 'contact', 'about', 'conversation'
 * 
 * Flow:
 * 1. User requests to show overlay -> activeOverlay is set -> triggers opening animation
 * 2. User requests to close -> triggers closing animation -> after animation, activeOverlay is set to null
 * 3. User switches overlays -> closes current (with animation) -> opens new (with animation)
 */
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react'

const OverlayContext = createContext()

export function OverlayManagerProvider({ children }) {
    const [activeOverlay, setActiveOverlay] = useState(null) // null or 'work' | 'contact' | 'about' | 'conversation'
    const [closingOverlay, setClosingOverlay] = useState(null) // Tracks which overlay is closing
    const [isAnimating, setIsAnimating] = useState(false) // Prevents state changes during animation

    /**
     * Request to show an overlay
     * If another overlay is active, close it first, then open the new one
     */
    const showOverlay = useCallback((overlayId) => {
        if (isAnimating) return // Don't allow changes during animation
        
        if (activeOverlay && activeOverlay !== overlayId) {
            // Another overlay is active - need to close it first
            console.log(`Switching from ${activeOverlay} to ${overlayId}`)
            setIsAnimating(true)
            
            // Close animation happens in the overlay component
            // After 500ms (animation duration), switch to new overlay
            setTimeout(() => {
                setActiveOverlay(overlayId)
                setIsAnimating(false)
            }, 500)
            
            // Trigger close on current overlay by temporarily setting to null
            setActiveOverlay(null)
        } else if (!activeOverlay) {
            // No overlay active - just open the new one
            console.log(`Opening ${overlayId}`)
            setActiveOverlay(overlayId)
        }
        // If same overlay is already active, do nothing
    }, [activeOverlay, isAnimating])

    /**
     * Request to close the active overlay
     * Triggers closing animation, then sets activeOverlay to null after animation completes
     */
    const closeOverlay = useCallback(() => {
        if (!activeOverlay || isAnimating) return
        
        console.log(`Closing ${activeOverlay}`)
        setIsAnimating(true)
        setClosingOverlay(activeOverlay) // Mark this overlay as closing
        setActiveOverlay(null) // Immediately set active to null
        
        // After animation completes, clear the closing state
        setTimeout(() => {
            setClosingOverlay(null)
            setIsAnimating(false)
        }, 500)
    }, [activeOverlay, isAnimating])

    return (
        <OverlayContext.Provider value={{ 
            activeOverlay, 
            closingOverlay,
            showOverlay, 
            closeOverlay,
            isAnimating 
        }}>
            {children}
        </OverlayContext.Provider>
    )
}

export function useOverlayManager() {
    const context = useContext(OverlayContext)
    if (!context) {
        throw new Error('useOverlayManager must be used within OverlayManagerProvider')
    }
    return context
}   